import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axiosInstance from '../axiosInstance';
import { useSelector } from 'react-redux';
import StoryViewer from '../components/story/StoryViewer';

const StoryContext = createContext({
  stories: [],
  loading: true,
  userStoryStatus: {},
  hasStory: () => false,
  hasUnviewedStory: () => false,
  openStoryViewer: () => {},
  refreshStories: () => {},
  markStoryViewed: () => {},
  deleteStory: () => {},
});

export const useStory = () => useContext(StoryContext);

export const StoryProvider = ({ children }) => {
  const [stories, setStories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [userStoryStatus, setUserStoryStatus] = useState({});
  const [viewerOpen, setViewerOpen] = useState(false);
  const [viewerStartIndex, setViewerStartIndex] = useState(0);

  const currentUser = useSelector(state => state.auth?.user);
  const token = useSelector(state => state.auth?.token);

  // Fetch stories feed
  const fetchStories = useCallback(async () => {
    if (!token) {
      setLoading(false);
      return;
    }

    try {
      const response = await axiosInstance.get('/stories/feed');
      if (response.data.success) {
        const storyData = response.data.data || [];
        setStories(storyData);

        // Build user story status map
        const statusMap = {};
        storyData.forEach((userStories) => {
          if (userStories.user?.id) {
            statusMap[userStories.user.id] = {
              hasStory: userStories.stories_count > 0,
              hasUnviewed: userStories.has_unviewed,
              storiesCount: userStories.stories_count,
              isOwn: userStories.is_own,
            };
          }
        });
        setUserStoryStatus(statusMap);
      }
    } catch (error) {
      console.error('Failed to fetch stories:', error);
    } finally {
      setLoading(false);
    }
  }, [token]);

  // Initial fetch
  useEffect(() => {
    if (token) {
      fetchStories();
    }
  }, [token, fetchStories]);

  // Check if a user has any active stories
  const hasStory = useCallback((userId) => {
    if (!userId) return false;
    return userStoryStatus[userId]?.hasStory || false;
  }, [userStoryStatus]);

  // Check if a user has unviewed stories
  const hasUnviewedStory = useCallback((userId) => {
    if (!userId) return false;
    return userStoryStatus[userId]?.hasUnviewed || false;
  }, [userStoryStatus]);

  // Open story viewer for a specific user
  const openStoryViewer = useCallback((userId) => {
    const userIndex = stories.findIndex(s => s.user?.id === userId);
    if (userIndex !== -1) {
      setViewerStartIndex(userIndex);
      setViewerOpen(true);
    }
  }, [stories]);

  // Mark a story as viewed
  const markStoryViewed = useCallback((storyId) => {
    setStories(prevStories =>
      prevStories.map(userStories => {
        const updatedStories = userStories.stories.map(story =>
          story.id === storyId ? { ...story, is_viewed: true } : story
        );
        const hasUnviewed = updatedStories.some(story => !story.is_viewed);

        return {
          ...userStories,
          stories: updatedStories,
          has_unviewed: hasUnviewed,
        };
      })
    );

    // Update user story status map
    setUserStoryStatus(prevStatus => {
      const newStatus = { ...prevStatus };
      stories.forEach(userStories => {
        const storyFound = userStories.stories.find(s => s.id === storyId);
        if (storyFound && userStories.user?.id) {
          const remainingUnviewed = userStories.stories.filter(
            s => s.id !== storyId && !s.is_viewed
          );
          newStatus[userStories.user.id] = {
            ...newStatus[userStories.user.id],
            hasUnviewed: remainingUnviewed.length > 0,
          };
        }
      });
      return newStatus;
    });
  }, [stories]);

  // Delete a story
  const deleteStory = useCallback((storyId) => {
    setStories(prevStories =>
      prevStories
        .map(userStories => ({
          ...userStories,
          stories: userStories.stories.filter(story => story.id !== storyId),
          stories_count: userStories.stories.filter(story => story.id !== storyId).length,
        }))
        .filter(userStories => userStories.stories.length > 0)
    );

    // Update user story status map
    setUserStoryStatus(prevStatus => {
      const newStatus = { ...prevStatus };
      Object.keys(newStatus).forEach(userId => {
        const userStories = stories.find(s => s.user?.id === parseInt(userId));
        if (userStories) {
          const remainingStories = userStories.stories.filter(s => s.id !== storyId);
          if (remainingStories.length === 0) {
            delete newStatus[userId];
          } else {
            newStatus[userId] = {
              ...newStatus[userId],
              hasStory: remainingStories.length > 0,
              storiesCount: remainingStories.length,
            };
          }
        }
      });
      return newStatus;
    });
  }, [stories]);

  // Handle story viewer close
  const handleViewerClose = () => {
    setViewerOpen(false);
  };

  return (
    <StoryContext.Provider
      value={{
        stories,
        loading,
        userStoryStatus,
        hasStory,
        hasUnviewedStory,
        openStoryViewer,
        refreshStories: fetchStories,
        markStoryViewed,
        deleteStory,
      }}
    >
      {children}

      {/* Global Story Viewer */}
      {viewerOpen && stories.length > 0 && (
        <StoryViewer
          isOpen={viewerOpen}
          onClose={handleViewerClose}
          stories={stories}
          initialUserIndex={viewerStartIndex}
          initialStoryIndex={0}
          onStoryViewed={markStoryViewed}
          onStoryDeleted={deleteStory}
        />
      )}
    </StoryContext.Provider>
  );
};

export default StoryContext;
